import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView, getBrowserGeolocation } from '../../utils/analytics';

interface UmamiTrackerProps {
  websiteId: string;
  scriptUrl?: string;
  domains?: string[];
  includeQueryParams?: boolean;
  enableGeolocation?: boolean;
}

/**
 * UmamiTracker - A component to integrate Umami analytics
 * 
 * @param websiteId - The website ID from Umami dashboard
 * @param scriptUrl - Optional custom script URL
 * @param domains - Optional list of domains to track
 * @param includeQueryParams - Whether to include query parameters in tracked URLs (default: false)
 * @param enableGeolocation - Whether to request and track browser geolocation (default: false)
 */
const UmamiTracker: React.FC<UmamiTrackerProps> = ({ 
  websiteId,
  scriptUrl = 'https://umami.nijulishe.co.ke/nijulishe-analytics.js',
  domains,
  includeQueryParams = false,
  enableGeolocation = false
}) => {
  const location = useLocation();
  const [geolocationPermission, setGeolocationPermission] = useState<string | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(false);

  // Check geolocation permission if enabled
  useEffect(() => {
    if (enableGeolocation && navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then(permissionStatus => {
        setGeolocationPermission(permissionStatus.state);
        
        permissionStatus.onchange = () => {
          setGeolocationPermission(permissionStatus.state);
        };
      });
    }
  }, [enableGeolocation]);

  // Load the Umami script
  useEffect(() => {
    // Check if script is already loaded
    const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);
    if (existingScript) {
      setScriptLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.defer = true;
    script.setAttribute('data-website-id', websiteId);
    script.setAttribute('data-auto-track', 'true');
    
    // Add domains attribute if provided
    if (domains && domains.length > 0) {
      script.setAttribute('data-domains', domains.join(','));
    }

    // Handle script load events
    script.onload = () => {
      setScriptLoaded(true);
      setScriptError(false);
    };

    script.onerror = () => {
      setScriptLoaded(false);
      setScriptError(true);
      console.error('Failed to load Umami analytics script');
    };

    // Add to document
    document.head.appendChild(script);

    // Cleanup on unmount
    return () => {
      const scriptElement = document.querySelector(`script[src="${scriptUrl}"]`);
      if (scriptElement) {
        document.head.removeChild(scriptElement);
      }
      setScriptLoaded(false);
    };
  }, [scriptUrl, websiteId, domains]);

  // Track page views when location changes
  useEffect(() => {
    // If script failed to load, don't try to track
    if (scriptError) return;

    const handlePageView = async () => {
      // Check if geolocation is enabled and permission is granted
      const shouldTrackGeolocation = enableGeolocation && 
        (geolocationPermission === 'granted' || geolocationPermission === null);
      
      await trackPageView(undefined, includeQueryParams, shouldTrackGeolocation);
    };

    // If script is loaded and umami is available, track immediately
    if (scriptLoaded && window.umami) {
      handlePageView();
      return;
    }

    // Otherwise, wait for umami to be available
    const interval = setInterval(() => {
      if (window.umami) {
        handlePageView();
        clearInterval(interval);
      }
    }, 500);

    // Clear interval after 5 seconds if umami doesn't load
    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!window.umami) {
        console.warn('Umami analytics not available after timeout');
      }
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [location, includeQueryParams, enableGeolocation, geolocationPermission, scriptLoaded, scriptError]);

  return null;
};

// Add TypeScript declaration for window.umami
declare global {
  interface Window {
    umami?: {
      trackView?: (url: string) => void;
      trackEvent?: (eventName: string, eventData?: any) => void;
      track?: (eventName?: string, eventData?: any) => void;
    };
  }
}

export default UmamiTracker; 